import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { CustomersService } from "@/services/customers.service";
import useModalStore from "@/store/useModalStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const EDIT_CUSTOMER_MODAL = "editCustomerModal";

interface EditCustomerModalProps {
  customer?: {
    id: string;
    name: string;
    email: string;
  };
}

export default function EditCustomerModal({
  customer,
}: EditCustomerModalProps) {
  const { t } = useTranslation(["common", "sales"]);
  const { modals, closeModal } = useModalStore();
  const queryClient = useQueryClient();

  const isOpen = modals[EDIT_CUSTOMER_MODAL] || false;

  const formSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t("common:required_field"))
          .email(t("common:invalid_email")),
      }),
    [t],
  );

  type FormValues = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: customer?.email || "",
    },
  });

  useEffect(() => {
    if (customer?.email) {
      reset({ email: customer.email });
    }
  }, [customer, reset]);

  const handleClose = () => {
    closeModal(EDIT_CUSTOMER_MODAL);
    reset({ email: customer?.email || "" });
  };

  const { mutate: updateCustomer, isPending } = useMutation({
    mutationFn: (data: FormValues) =>
      CustomersService.updateCustomer(customer!.id, { email: data.email }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getSellInfo"] });
      toast({
        description: t("sales:customer_update_success"),
        variant: "success",
      });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        description:
          error?.response?.data?.message || t("sales:customer_update_error"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormValues) => {
    if (!customer?.id) return;
    updateCustomer(data);
  };

  if (!customer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden border-colorBorder">
        <DialogHeader className="p-6 pb-4 border-b border-colorBorder">
          <DialogTitle className="text-xl font-semibold">
            {t("sales:edit_customer_email")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 pt-2 space-y-4">
            <Alert
              variant="warning"
              className="bg-yellow-500/10 border-yellow-500/20 text-yellow-500"
            >
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <AlertTitle>{t("common:attention")}</AlertTitle>
              <AlertDescription>
                {t("sales:edit_email_warning_once")}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium text-muted-foreground"
              >
                {t("common:email")}
              </label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                className="h-10 text-sm transition-all border-colorBorder placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary/50"
                placeholder="exemplo@email.com"
              />
              {errors.email && (
                <span className="text-xs font-medium text-red-500 animate-in slide-in-from-top-1 fade-in-0">
                  {errors.email.message}
                </span>
              )}
            </div>
          </div>

          <DialogFooter className="p-6 border-t gap-3 border-colorBorder">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("common:saving")}
                </>
              ) : (
                <>{t("common:save")}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
